import React, { useState } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Form, Select, InputNumber, Input, message, Empty } from 'antd';
import { PlusOutlined, DeleteOutlined, NodeIndexOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  useOperationDependencies,
  useCreateOperationDependency,
  useDeleteOperationDependency,
  useBOMOperations,
} from '@/hooks/useManufacturingPhase2';
import { OperationDependency, DependencyType } from '@/services/manufacturing-phase2.service';

const OperationDependenciesPage: React.FC = () => {
  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedBOM, setSelectedBOM] = useState<string>('');

  // Mock BOM selector
  const bomOptions = [
    { id: 'bom-1', label: 'BOM-001 - Product A' },
    { id: 'bom-2', label: 'BOM-002 - Product B' },
  ];

  // API hooks
  const { data: dependencies = [], isLoading } = useOperationDependencies(selectedBOM);
  const { data: bomOperations = [] } = useBOMOperations(selectedBOM);
  const createDependency = useCreateOperationDependency();
  const deleteDependency = useDeleteOperationDependency();

  const handleCreate = () => {
    form.resetFields();
    form.setFieldsValue({
      bom_id: selectedBOM,
      dependency_type: DependencyType.FINISH_TO_START,
      lead_time_hours: 0,
      lag_time_hours: 0,
    });
    setModalVisible(true);
  };

  const handleDelete = (id: string) => {
    Modal.confirm({
      title: 'Delete Dependency',
      content: 'Are you sure you want to delete this dependency?',
      onOk: () => deleteDependency.mutate(id),
    });
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await createDependency.mutateAsync(values);
      setModalVisible(false);
      form.resetFields();
    } catch (error) {
      console.error('Form validation error:', error);
    }
  };

  const columns: ColumnsType<OperationDependency> = [
    {
      title: 'Operation (Child)',
      dataIndex: ['operation', 'sequence_number'],
      key: 'operation',
      width: 250,
      render: (_, record) => (
        <div>
          <Tag color="blue">Seq {record.operation?.sequence_number}</Tag>
          <span>{record.operation?.operation?.operation_name || 'Unknown'}</span>
        </div>
      ),
    },
    {
      title: 'Depends On (Parent)',
      dataIndex: ['depends_on_operation', 'sequence_number'],
      key: 'depends_on',
      width: 250,
      render: (_, record) => (
        <div>
          <Tag color="orange">Seq {record.depends_on_operation?.sequence_number}</Tag>
          <span>{record.depends_on_operation?.operation?.operation_name || 'Unknown'}</span>
        </div>
      ),
    },
    {
      title: 'Dependency Type',
      dataIndex: 'dependency_type',
      key: 'dependency_type',
      width: 180,
      render: (type: DependencyType) => {
        const colors: Record<DependencyType, string> = {
          [DependencyType.SEQUENTIAL]: 'blue',
          [DependencyType.PARALLEL]: 'green',
          [DependencyType.CONDITIONAL]: 'orange',
          [DependencyType.START_TO_START]: 'cyan',
          [DependencyType.FINISH_TO_START]: 'purple',
          [DependencyType.FINISH_TO_FINISH]: 'magenta',
        };
        return <Tag color={colors[type]}>{type}</Tag>;
      },
    },
    {
      title: 'Lead Time (hrs)',
      dataIndex: 'lead_time_hours',
      key: 'lead_time_hours',
      width: 130,
      align: 'right',
      render: (val: number) => val.toFixed(2),
    },
    {
      title: 'Lag Time (hrs)',
      dataIndex: 'lag_time_hours',
      key: 'lag_time_hours',
      width: 130,
      align: 'right',
      render: (val: number) => val.toFixed(2),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleDelete(record.id)}
        >
          Delete
        </Button>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card
        title={
          <Space>
            <NodeIndexOutlined />
            <span>Operation Dependencies</span>
          </Space>
        }
        extra={
          <Space>
            <Select
              placeholder="Select BOM"
              style={{ width: 250 }}
              value={selectedBOM || undefined}
              onChange={setSelectedBOM}
              options={bomOptions.map(b => ({ label: b.label, value: b.id }))}
            />
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate} disabled={!selectedBOM || bomOperations.length < 2}>
              Add Dependency
            </Button>
          </Space>
        }
      >
        {!selectedBOM ? (
          <Empty description="Please select a BOM to view dependencies" />
        ) : bomOperations.length < 2 ? (
          <Empty description="At least 2 operations are required to create dependencies" />
        ) : (
          <Table
            columns={columns}
            dataSource={dependencies}
            rowKey="id"
            loading={isLoading}
            scroll={{ x: 1200 }}
            pagination={false}
          />
        )}
      </Card>

      <Modal
        title="Add Operation Dependency"
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSubmit}
        width={700}
        confirmLoading={createDependency.isPending}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="bom_id" hidden>
            <Select />
          </Form.Item>

          <Form.Item
            name="operation_id"
            label="Dependent Operation (Child)"
            rules={[{ required: true, message: 'Please select the dependent operation' }]}
            tooltip="This operation will wait for the prerequisite"
          >
            <Select
              showSearch
              placeholder="Select operation"
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={bomOperations.map((op: any) => ({
                label: `Seq ${op.sequence_number}: ${op.operation?.operation_name || 'Unknown'}`,
                value: op.id,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="depends_on_operation_id"
            label="Prerequisite Operation (Parent)"
            rules={[{ required: true, message: 'Please select the prerequisite operation' }]}
            tooltip="This operation must be completed first"
          >
            <Select
              showSearch
              placeholder="Select operation"
              filterOption={(input, option) =>
                String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={bomOperations.map((op: any) => ({
                label: `Seq ${op.sequence_number}: ${op.operation?.operation_name || 'Unknown'}`,
                value: op.id,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="dependency_type"
            label="Dependency Type"
            rules={[{ required: true }]}
          >
            <Select>
              {Object.values(DependencyType).map(type => (
                <Select.Option key={type} value={type}>{type}</Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Space style={{ width: '100%' }} size="large">
            <Form.Item
              name="lead_time_hours"
              label="Lead Time (hours)"
              tooltip="Time before dependent operation can start"
            >
              <InputNumber min={0} step={0.5} style={{ width: 200 }} />
            </Form.Item>

            <Form.Item
              name="lag_time_hours"
              label="Lag Time (hours)"
              tooltip="Minimum time after prerequisite before starting"
            >
              <InputNumber min={0} step={0.5} style={{ width: 200 }} />
            </Form.Item>
          </Space>

          <Form.Item name="condition_description" label="Condition Description (if Conditional)">
            <Input.TextArea rows={2} placeholder="Describe the condition..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OperationDependenciesPage;

